import { useEffect, useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchAgents, fetchMoreAgents, createAgent, deleteAgent } from '../slices/agentsSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import AgentsTable from '../components/AgentsTable'
import NewAgentDialog from '../components/NewAgentDialog'
import './AgentsPage.css'

function AgentsPage() {
  const dispatch = useAppDispatch()
  const { items: agents, loading, error, hasMore, page, total } = useAppSelector((state) => state.agents)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    dispatch(fetchAgents())
  }, [dispatch])

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true)
    await dispatch(fetchMoreAgents(page + 1))
    setLoadingMore(false)
  }, [dispatch, page])

  const handleCreateAgent = useCallback(
    async (data: {
      name: string
      tool_command: string
      prompt: string
    }) => {
      await dispatch(createAgent(data)).unwrap()
    },
    [dispatch]
  )

  const handleDeleteAgent = useCallback(
    (id: string) => {
      dispatch(deleteAgent(id))
    },
    [dispatch]
  )

  return (
    <div data-testid="agents-page" className="agents-page">
      <SidebarNavigation />

      <main className="app-page-main">
        <header className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner app-page-sticky-header-inner--row">
            <h1 className="app-page-title agents-title">Configured Agents</h1>
            <button
              data-testid="new-agent-btn"
              className="new-agent-btn"
              onClick={() => setDialogOpen(true)}
            >
              + New Agent
            </button>
          </div>
        </header>

        {loading && (
          <div data-testid="agents-loading" className="agents-loading">
            Loading agents...
          </div>
        )}

        {error && (
          <div data-testid="agents-error" className="agents-error">
            {error}
          </div>
        )}

        {!loading && !error && agents.length === 0 && (
          <div data-testid="agents-empty" className="agents-empty">
            No agents configured yet. Create an agent to get started.
          </div>
        )}

        {!loading && agents.length > 0 && (
          <AgentsTable agents={agents} onDelete={handleDeleteAgent} />
        )}

        {hasMore && !loading && (
          <div className="app-load-more-wrap">
            <button
              data-testid="load-more-agents"
              type="button"
              className="app-load-more-btn load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : `Load More (${agents.length} of ${total})`}
            </button>
          </div>
        )}

        <NewAgentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleCreateAgent}
        />
      </main>
    </div>
  )
}

export default AgentsPage
